import { useCallback, useEffect, useRef, useState } from "react";

/** An API failure with the sentence the server sent, shown as is. */
export class ApiError extends Error {
  constructor(message: string, readonly status: number, readonly body: unknown) {
    super(message);
  }
}

let onUnauthorized: () => void = () => {
  window.location.assign(`/signin?return=${encodeURIComponent(location.pathname + location.search)}`);
};
export function setUnauthorizedHandler(fn: () => void) {
  onUnauthorized = fn;
}

export async function api<T = unknown>(path: string, init: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const res = await fetch(path.startsWith("/") ? path : `/api/${path}`, {
    method: init.method ?? (init.body === undefined ? "GET" : "POST"),
    headers: init.body === undefined ? { Accept: "application/json" } : { "Content-Type": "application/json", Accept: "application/json" },
    body: init.body === undefined ? undefined : JSON.stringify(init.body),
    credentials: "same-origin",
    signal: init.signal,
  });
  const text = await res.text();
  let data: unknown = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (res.status === 401) {
    onUnauthorized();
    throw new ApiError("Your session has ended.", 401, data);
  }
  if (!res.ok) {
    const msg = (data as { message?: string })?.message ?? (typeof data === "string" && data ? data.slice(0, 200) : `The server answered ${res.status}.`);
    throw new ApiError(msg, res.status, data);
  }
  return data as T;
}

export const post = <T = unknown>(path: string, body: unknown = {}) => api<T>(path, { method: "POST", body });
export const put = <T = unknown>(path: string, body: unknown) => api<T>(path, { method: "PUT", body });
export const patch = <T = unknown>(path: string, body: unknown) => api<T>(path, { method: "PATCH", body });
export const del = <T = unknown>(path: string) => api<T>(path, { method: "DELETE" });

type State<T> = { data: T | undefined; error: ApiError | null; loading: boolean; updatedAt?: number };

const cache = new Map<string, unknown>();

/**
 * Reads one endpoint, keeps the last answer per path so returning to a page
 * shows it at once, and refreshes it every `poll` ms while the tab is visible.
 */
export function useApi<T>(path: string | null, opts: { poll?: number } = {}) {
  const [state, setState] = useState<State<T>>({ data: path ? (cache.get(path) as T | undefined) : undefined, error: null, loading: !!path });
  const ctrl = useRef<AbortController | null>(null);
  const load = useCallback(
    async (quiet = false) => {
      if (!path) return;
      ctrl.current?.abort();
      const c = new AbortController();
      ctrl.current = c;
      if (!quiet) setState((s) => ({ ...s, loading: true }));
      try {
        const data = await api<T>(path, { signal: c.signal });
        cache.set(path, data);
        if (!c.signal.aborted) setState({ data, error: null, loading: false, updatedAt: Date.now() });
      } catch (e) {
        if (c.signal.aborted) return;
        setState((s) => ({ ...s, error: e as ApiError, loading: false }));
      }
    },
    [path],
  );
  useEffect(() => {
    setState({ data: path ? (cache.get(path) as T | undefined) : undefined, error: null, loading: !!path });
    load();
    return () => ctrl.current?.abort();
  }, [path, load]);
  useEffect(() => {
    if (!opts.poll || !path) return;
    const t = setInterval(() => {
      if (document.visibilityState === "visible") load(true);
    }, opts.poll);
    return () => clearInterval(t);
  }, [opts.poll, path, load]);
  return { ...state, reload: () => load(true), setData: (d: T) => setState((s) => ({ ...s, data: d })) };
}
